import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Heart, Minus, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  email: "",
  attending: "yes",
  guest_count: 1,
  // Unified list of party members. Index 0 = primary respondent (you).
  party_members: [{ name: "", dietary: "" }],
  song_request: "",
  message: "",
};

export default function RSVPForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const guestCount = useMemo(
    () => Math.max(1, Math.min(10, parseInt(form.guest_count, 10) || 1)),
    [form.guest_count]
  );

  const update = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e?.target ? e.target.value : e }));

  const setGuestCount = (n) => {
    const next = Math.min(10, Math.max(1, n));
    setForm((p) => {
      const arr = [...(p.party_members || [])];
      while (arr.length < next) arr.push({ name: "", dietary: "" });
      arr.length = next;
      return { ...p, guest_count: next, party_members: arr };
    });
  };

  const setMemberField = (idx, key, value) => {
    setForm((p) => {
      const arr = [...(p.party_members || [])];
      const cur = arr[idx] || { name: "", dietary: "" };
      arr[idx] = { ...cur, [key]: value };
      return { ...p, party_members: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const members = (form.party_members || []).slice(0, guestCount);
    const primary = members[0]?.name?.trim();
    if (!primary) {
      toast.error("Please share your full name.");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Please share your email so we can confirm your reply.");
      return;
    }
    if (members.slice(1).some((m) => !m?.name?.trim())) {
      toast.error("Please add the full name of everyone in your party.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend expects: full_name (primary), party_members = rest of party
      const restOfParty = members.slice(1).map((m) => ({
        name: (m.name || "").trim(),
        dietary: form.attending === "yes" ? (m.dietary || "").trim() : "",
      }));
      const payload = {
        full_name: primary,
        email: form.email.trim(),
        attending: form.attending,
        guest_count: guestCount,
        party_members: restOfParty,
        dietary_requirements:
          form.attending === "yes" ? (members[0]?.dietary || "").trim() : "",
        song_request: form.attending === "yes" ? form.song_request : "",
        message: form.message,
      };
      await axios.post(`${API}/rsvp`, payload);
      setSubmitted(true);
      toast.success(
        form.attending === "yes"
          ? "Thank you! We can't wait to celebrate with you."
          : "Thank you for letting us know — you'll be missed."
      );
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your RSVP. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const primaryName =
      (form.party_members?.[0]?.name || "").trim().split(" ")[0] || "friend";
    return (
      <section
        id="rsvp"
        data-testid="rsvp-section"
        className="bg-[#6A1E2D] text-[#FAF9F6] py-32 md:py-40 px-6"
      >
        <div className="max-w-xl mx-auto text-center" data-testid="rsvp-success">
          <Heart className="w-7 h-7 mx-auto mb-6 text-[#A3C1DA]" strokeWidth={1.4} />
          <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-4">RSVP Received</p>
          <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] mb-6">
            Thank you, {primaryName}.
          </h2>
          <p className="text-[#FAF9F6]/80 leading-relaxed mb-10">
            {form.attending === "yes"
              ? "We're delighted you'll be joining us at Glenlo Abbey."
              : "Thank you for letting us know. We'll be raising a glass to you on the day."}
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setForm(initialState);
            }}
            data-testid="rsvp-reset-button"
            className="text-xs tracking-luxe uppercase border-b border-[#FAF9F6]/40 pb-1 hover:border-[#FAF9F6] transition-colors"
          >
            Submit another response
          </button>
        </div>
      </section>
    );
  }

  const declining = form.attending === "no";

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="bg-[#6A1E2D] text-[#FAF9F6] py-24 md:py-32 px-6"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-display italic text-lg md:text-xl text-[#FAF9F6]/85 leading-relaxed max-w-xl mx-auto">
            Michelle &amp; Niall would be delighted to share their wedding day
            with you on Friday, the 4<sup className="text-[10px]">th</sup> of June 2027,
            at St. Joseph's Church, Galway, with reception to follow at Glenlo Abbey.
          </p>
          <p className="mt-6 text-[#FAF9F6]/80 leading-relaxed max-w-xl mx-auto">
            Kindly reply by <strong className="font-medium">1st March 2027</strong> so we can finalise
            the seating chart and our caterers can make sure everyone's well fed.
          </p>
          <p className="mt-3 text-[#FAF9F6]/70 text-sm leading-relaxed max-w-xl mx-auto">
            A gentle note: please only include children or plus-ones if they were named on your invitation from Michelle &amp; Niall. Thank you for understanding.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="rsvp-form" className="space-y-10">
          {/* Contact email */}
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] block mb-2">
              Email (for our records)
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="rsvp-email-input"
              className="field-underline-dark"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          {/* Attending */}
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] block mb-4">
              Will you be attending?
            </label>
            <div
              role="radiogroup"
              aria-label="Will you be attending?"
              data-testid="rsvp-attending-radio"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {[
                { value: "yes", label: "Joyfully accept" },
                { value: "no", label: "Regretfully decline" },
              ].map((opt) => {
                const selected = form.attending === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() =>
                      setForm((p) => ({ ...p, attending: opt.value }))
                    }
                    data-testid={`rsvp-attending-${opt.value}`}
                    className={
                      "flex items-center gap-4 px-5 py-4 border transition-all duration-200 text-left " +
                      (selected
                        ? "bg-[#FAF9F6] text-[#6A1E2D] border-[#FAF9F6] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.4)]"
                        : "bg-transparent text-[#FAF9F6] border-[#FAF9F6]/35 hover:border-[#FAF9F6]/70 hover:bg-[#FAF9F6]/5")
                    }
                  >
                    <span
                      className={
                        "shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors " +
                        (selected ? "border-[#6A1E2D]" : "border-[#FAF9F6]/60")
                      }
                    >
                      {selected && (
                        <span className="w-2.5 h-2.5 rounded-full bg-[#6A1E2D]" />
                      )}
                    </span>
                    <span className="font-body text-sm md:text-base">
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Party size stepper */}
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] block mb-3">
              {declining
                ? "Number declining (including yourself)"
                : "Number in your party (including yourself)"}
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setGuestCount(guestCount - 1)}
                disabled={guestCount <= 1}
                data-testid="rsvp-guest-decrement"
                aria-label="Decrease party size"
                className="w-10 h-10 inline-flex items-center justify-center border border-[#FAF9F6]/40 hover:border-[#FAF9F6] hover:bg-[#FAF9F6]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="w-4 h-4" strokeWidth={1.6} />
              </button>
              <span
                data-testid="rsvp-guest-count-display"
                className="font-display text-3xl tabular-nums w-10 text-center"
              >
                {guestCount}
              </span>
              <button
                type="button"
                onClick={() => setGuestCount(guestCount + 1)}
                disabled={guestCount >= 10}
                data-testid="rsvp-guest-increment"
                aria-label="Increase party size"
                className="w-10 h-10 inline-flex items-center justify-center border border-[#FAF9F6]/40 hover:border-[#FAF9F6] hover:bg-[#FAF9F6]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>
          </div>

          {/* Unified party list */}
          <div data-testid="rsvp-party-list" className="space-y-6 pt-2">
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-2">
                {declining ? "Who is declining" : "Your party"}
              </p>
              <p className="text-xs text-[#FAF9F6]/65">
                {declining
                  ? "So we know exactly who can't make it — please share the full name of each person, starting with yourself."
                  : "Full name as you'd like it on the place card, plus any dietary requirements, for each person in your party."}
              </p>
            </div>

            <div className="space-y-6">
              {Array.from({ length: guestCount }).map((_, idx) => {
                const m =
                  (form.party_members || [])[idx] || { name: "", dietary: "" };
                const label = idx === 0 ? "Guest 1 · You" : `Guest ${idx + 1}`;
                return (
                  <div
                    key={idx}
                    className={
                      "grid grid-cols-1 gap-x-8 gap-y-4 pb-5 border-b border-[#FAF9F6]/15 last:border-b-0 " +
                      (declining ? "" : "md:grid-cols-2")
                    }
                    data-testid={`rsvp-party-row-${idx}`}
                  >
                    <div>
                      <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA]/80 block mb-2">
                        {label} · Full name
                      </label>
                      <input
                        type="text"
                        value={m.name}
                        onChange={(e) => setMemberField(idx, "name", e.target.value)}
                        data-testid={`rsvp-party-${idx}-name`}
                        className="field-underline-dark"
                        placeholder="Full name"
                        autoComplete={idx === 0 ? "name" : "off"}
                      />
                    </div>
                    {!declining && (
                      <div>
                        <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA]/80 block mb-2">
                          {label} · Dietary requirements
                        </label>
                        <input
                          type="text"
                          value={m.dietary}
                          onChange={(e) =>
                            setMemberField(idx, "dietary", e.target.value)
                          }
                          data-testid={`rsvp-party-${idx}-dietary`}
                          className="field-underline-dark"
                          placeholder="None, vegetarian, allergies, etc."
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!declining && (
            <div>
              <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] block mb-2">
                Song requests
              </label>
              <input
                type="text"
                value={form.song_request}
                onChange={update("song_request")}
                data-testid="rsvp-song-input"
                className="field-underline-dark"
                placeholder="A song to get you on the dance floor"
              />
            </div>
          )}

          <div>
            <label className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] block mb-2">
              A note for the bride &amp; groom (optional)
            </label>
            <textarea
              rows={3}
              value={form.message}
              onChange={update("message")}
              data-testid="rsvp-message-input"
              className="field-underline-dark resize-none"
              placeholder="Share a wish, a memory, or a hello"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="rsvp-submit-button"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#FAF9F6] text-[#6A1E2D] hover:bg-[#A3C1DA] hover:text-[#1A1818] transition-colors duration-300 px-12 py-4 text-xs tracking-luxe uppercase disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  Send our reply
                  <span aria-hidden>→</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
